import { css } from '@emotion/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { isEmpty, keys, values } from 'underscore';
import { useSelector } from 'react-redux';

import t from 'react-translate';

import { fetchCohortsQuestionSet, fetchQuestionSet, resetQuestionSets } from 'redux/actions/analytics';
import { useAppDispatch } from 'redux/store';
import { getQuestionSets } from 'redux/selectors/analytics';

import NvDropdown, {
  NvDropdownOption, NvDropdownButtonStyle, NvDropdownAlign,
  NvDropdownAccordionHeader,
} from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { AnalyticsPageType } from 'redux/schemas/app/analytics';

const keyMapping = {
  quizzes: 'QUIZ',
  timedExams: 'TIMED_EXAM',
  surveys: 'SURVEY',
};

const styles = css`
  align-items: center;
  display: flex;
  margin-left: 20px;

  .bs4-dropdown {
    .bs4-dropdown-menu {
      min-width: 300px !important;
        &.show {
        transform: translate3d(0%, 35px, 0px) !important;
        left: ${standardSpacing}px !important;
      }
      .bs4-dropdown-item {
        white-space: normal !important;
      }
    }
    .accordion-collapse {
      overflow-y: auto;
    }
    .expanded {
      max-height: 300px !important;
    }
  }
`;

type QuizSurveyAnalyticsTabProps = {
  onSelect: (id) => void,
  selectedId: number,
  analyticsPageType?: AnalyticsPageType
};

const QuizAndSurveyAnalyticsTab = ({
  onSelect,
  selectedId,
  analyticsPageType = AnalyticsPageType.COURSE_ANALYTICS,
}: QuizSurveyAnalyticsTabProps) => {
  const institutionId = useSelector(state => state.app.currentInstitutionId);
  const catalogId = useSelector((state) => state.app.currentCatalogId);
  const questionSets = useSelector(state => getQuestionSets(state, analyticsPageType));

  const dropdownRef = React.useRef<HTMLDivElement>();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (analyticsPageType === AnalyticsPageType.COHORT_ANALYTICS) {
      dispatch(fetchCohortsQuestionSet({ institutionId, catalogId }));
    } else {
      dispatch(fetchQuestionSet({ institutionId, catalogId }));
    }
  }, [analyticsPageType, catalogId, dispatch, institutionId]);

  const onSelectQuestionSet = useCallback((questionSetId) => {
    onSelect(questionSetId);
  }, [onSelect]);

  const totalNumberofQuestionSets = useMemo(() => values(questionSets)?.flat()?.length ?? 0, [questionSets]);

  const categoriesWithItems = useMemo(() => (
    keys(questionSets).filter(key => questionSets[key]?.length > 0).length
  ), [questionSets]);

  const getCategories = useMemo(() => {
    const categories: NvDropdownOption[] = keys(questionSets)?.map(key => ({
      type: 'accordion-header',
      title: t.COURSE_ANALYTICS[keyMapping[key]](),
      key,
      expanded: (categoriesWithItems === 1 && questionSets[key]?.length > 0)
        || (totalNumberofQuestionSets < 10),
      items: questionSets[key].map(item => ({
        type: 'text',
        text: item.title,
        id: item.id,
        key,
        isChecked: item.id === selectedId,
        callback: () => onSelectQuestionSet(item.id),
      })),
    }));
    return categories;
  }, [categoriesWithItems, onSelectQuestionSet, questionSets, selectedId, totalNumberofQuestionSets]);

  const findInitialIndex = useMemo(() => {
    let initialIndex = -1;

    getCategories.find((value: NvDropdownAccordionHeader) => {
      initialIndex = value.items.findIndex(item => item.id === selectedId);
      return initialIndex >= 0; // Return true if the index is valid
    });
    return initialIndex;
  }, [getCategories, selectedId]);

  useEffect(() => () => {
    dispatch(resetQuestionSets());
  }, [dispatch]);

  if (isEmpty(questionSets)) return null;

  return (
    <div
      className={selectedId ? 'bottom-border' : ''}
      css={styles}
      ref={dropdownRef}
    >
      <NvDropdown
        buttonStyle={NvDropdownButtonStyle.CUSTOM}
        items={getCategories}
        align={NvDropdownAlign.LEFT}
        noMenuPadding
        initialIndex={findInitialIndex}
        showSelectedIndicator={!!selectedId}
        altLabel={t.COURSE_ANALYTICS.SURVEY_AND_QUIZ()}
        minWidth={300}
        customTarget={() => (
          <div
            className='d-flex card-title align-items-center px-4'
          >
            {t.COURSE_ANALYTICS.SURVEY_AND_QUIZ()}
            <NvIcon icon='dropdown-arrow' size='xss-smallest' className='ml-3 mt-2' />
          </div>
        )}
      />
    </div>
  );
};

export default QuizAndSurveyAnalyticsTab;
